import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { getCountry } from '@/utils/i18n';
import {
  sendRingSizer,
  postKlaviyo,
  klaviyoIdentify,
  flushKlaviyoIdentifyForNavigation,
} from '@/services/forms.service';
import useStore from '@/store/useStore';
import beTwMerge from '@/utils/beTwMerge';
import Countries from '../../../public/data/countries.json';
import Select, { Option } from '../Select/Select';
import Button from '../Button/Button';

export default function RingSizeForm() {
  const {
    register,
    getValues,
    trigger,
    setFocus,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  const errorMsg = (field: string) => `${field} is required.`;
  const [btnTxt, setBtnTxt] = useState('Request Free Ring Sizer');
  const [isSubscribed, setIsSubscribed] = useState(true);
  const [agreement, setAgreement] = useState({ isAgree: false, hasAgreed: true });
  const isGB = getCountry().code === 'GB';
  const selectedCountry = Countries.find(c => c.value === getCountry().code)!;
  const [states, setSatates] = useState<{ label: string; value: number }[]>(selectedCountry.states);
  const [selectedState, setSelectedState] = useState<Option | null>(null);
  const user = useStore(state => state.user);
  const getFirstError = useCallback(
    () => Object.keys(errors).reduce<any>((field, a) => (errors[field] ? field : a), null),
    [errors]
  );

  async function validateForm() {
    const isFormValid = await trigger();
    const formData = {
      ...getValues(),
      email: getValues().email.trim(),
      subscribe_to_newsletter: isSubscribed ? 'T' : 'F',
      data_type: 3,
    };

    if ((isGB || user?.isEuropeanUnionMember) && !agreement.isAgree) {
      setAgreement(v => ({ ...v, hasAgreed: false }));
      return;
    }

    if (isFormValid) {
      setBtnTxt('Sending');
      if (isSubscribed) {
        postKlaviyo('Ring Sizer Request Page', formData.email);
      }
      const identifyOpts = {
        email: formData.email,
        firstName: (formData as any).first_name,
        lastName: (formData as any).last_name,
      };
      klaviyoIdentify(identifyOpts);
      sendRingSizer(formData)
        .then(async () => {
          reset();
          setBtnTxt('Sent Successfully');
          // Wait for identify to finish before hard navigation so the Klaviyo
          // cookie is set on /ring_sizer_success/ (isIdentified() === true).
          await flushKlaviyoIdentifyForNavigation();
          try {
            sessionStorage.setItem(
              'be_klaviyo_pending_identify',
              JSON.stringify({
                email: identifyOpts.email,
                firstName: identifyOpts.firstName ?? null,
                lastName: identifyOpts.lastName ?? null,
                fromAuth: false,
              })
            );
          } catch {
            // sessionStorage may be unavailable
          }
          window.location.href = '/ring_sizer_success/';
        })
        .catch(() => {
          // eslint-disable-next-line no-alert
          alert('Sent failed. Please try again.');
        });
    } else {
      const firstError = getFirstError();
      if (getFirstError()) setFocus<any>(firstError);
    }
  }
  useEffect(() => {
    const firstError = getFirstError();
    if (firstError) {
      setFocus<any>(firstError);
    }
  }, [getFirstError, setFocus]);

  return (
    <form className="form-1 tw-mt-10 tw-font-book">
      <div className="tw-flex md:tw-gap-[10px] tw-flex-col md:tw-flex-row">
        <div className={beTwMerge(errors.first_name && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4 tw-w-full')}>
          <input
            {...register('first_name', {
              required: errorMsg('First Name'),
            })}
            tabIndex={1}
            type="text"
            id="input_first_name"
            className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
            placeholder=" "
            maxLength={32}
          />
          <label
            htmlFor="input_first_name"
            className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
          >
            First Name
          </label>
          {errors.first_name?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
              {errors?.first_name.message as string}
            </p>
          )}
        </div>

        <div className={beTwMerge(errors.last_name && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4 tw-w-full')}>
          <input
            tabIndex={2}
            {...register('last_name', { required: errorMsg('Last Name') })}
            type="text"
            id="input_last_name"
            className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
            placeholder=" "
            maxLength={32}
          />
          <label
            htmlFor="input_last_name"
            className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
          >
            Last Name
          </label>
          {errors.last_name?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
              {errors?.last_name.message as string}
            </p>
          )}
        </div>
      </div>

      <div className={beTwMerge(errors.email && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4')}>
        <input
          tabIndex={3}
          {...register('email', {
            required: errorMsg('Email Address'),
            validate: {
              isValid: v =>
                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,3}$/.test(v.trim()) || 'Invalid Email Address',
            },
          })}
          type="text"
          id="input_email"
          className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
          placeholder=" "
          maxLength={75}
        />
        <label
          htmlFor="input_email"
          className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
        >
          Email Address
        </label>
        {errors.email?.message && (
          <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
            {errors?.email.message as string}
          </p>
        )}
      </div>
      <div className={beTwMerge(errors.address_line_1 && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4')}>
        <input
          tabIndex={4}
          {...register('address_line_1', {
            required: errorMsg('Address Line 1'),
          })}
          type="text"
          id="input_address_line_1"
          className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
          placeholder=" "
        />
        <label
          htmlFor="input_address_line_1"
          className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
        >
          Address Line 1
        </label>
        {errors.address_line_1?.message && (
          <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
            {errors?.address_line_1.message as string}
          </p>
        )}
      </div>
      <div className="tw-group tw-relative tw-z-0 tw-mb-4">
        <input
          {...register('address_line_2')}
          type="text"
          tabIndex={5}
          id="input_address_line_2"
          className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
          placeholder=" "
        />
        <label
          htmlFor="input_address_line_2"
          className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
        >
          Apt # / Suite
        </label>
      </div>
      <div className="tw-flex md:tw-gap-[10px] tw-flex-col md:tw-flex-row">
        <div className={beTwMerge(errors.city && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4 tw-w-full')}>
          <input
            {...register('city', {
              required: errorMsg('City'),
            })}
            tabIndex={6}
            type="text"
            id="input_city"
            className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
            placeholder=" "
            maxLength={70}
          />
          <label
            htmlFor="input_city"
            className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
          >
            City
          </label>
          {errors.city?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
              {errors?.city.message as string}
            </p>
          )}
        </div>
        <div className="tw-w-full tw-mb-4">
          <Select
            items={states}
            tabIndex={7}
            selectedOption={selectedState}
            placeholder="Select State/Province"
            onChange={option => {
              setValue('state', option.value);
              setSelectedState(option);
            }}
            className={beTwMerge(errors.state && 'is-error', '')}
            register={register('state', {
              required: errorMsg('State'),
            })}
          />
          {errors.state?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">{errors?.state.message as string}</p>
          )}
        </div>
      </div>

      <div className="tw-flex md:tw-gap-[10px] tw-flex-col md:tw-flex-row">
        <div
          className={beTwMerge(errors.zip_postal_code && 'is-error', 'tw-group tw-relative tw-z-0 tw-mb-4 tw-w-full')}
        >
          <input
            tabIndex={8}
            {...register('zip_postal_code', {
              required: errorMsg('Zip/Post Code'),
            })}
            type="text"
            id="input_zip_postal_code"
            className="border-normal tw-peer tw-block tw-h-10 tw-w-full tw-appearance-none tw-rounded-none tw-border tw-border-gray tw-bg-transparent tw-px-2 tw-pb-0 tw-pt-4 tw-text-sm/5 tw-outline-none tw-outline-0 focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
            placeholder=" "
            maxLength={10}
          />
          <label
            htmlFor="input_zip_postal_code"
            className="tw-pointer-events-none tw-absolute tw-left-2 tw-top-2.5 tw-origin-[0] -tw-translate-y-2 tw-scale-75 tw-transform tw-text-sm tw-font-normal tw-text-grey500 tw-duration-300 group-[.is-error]:!tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-placeholder-shown:tw-text-darkGrey peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75 peer-focus:tw-text-grey500"
          >
            Zip/Postal Code
          </label>
          {errors.zip_postal_code?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-grey500 group-[.is-error]:tw-block group-[.is-error]:tw-text-error">
              {errors?.zip_postal_code.message as string}
            </p>
          )}
        </div>
        <div className="tw-w-full tw-mb-4">
          <Select
            items={Countries}
            placeholder="Select Country"
            tabIndex={9}
            selectedOption={selectedCountry}
            register={register('country', {
              required: errorMsg('Country'),
            })}
            className={beTwMerge(errors.country && 'is-error', '')}
            onChange={({ value }) => {
              setSelectedState(null);
              setValue('country', value);
              setSatates(Countries.find(c => c.value === value)?.states!);
            }}
          />
          {errors.country?.message && (
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">{errors?.country.message as string}</p>
          )}
        </div>
      </div>

      <div className="tw-group tw-space-y-2 tw-mb-4">
        <label
          htmlFor="input_message"
          className="tw-mb-0 tw-text-sm tw-font-normal group-[.is-error]:tw-text-error peer-placeholder-shown:tw-translate-y-0 peer-placeholder-shown:tw-scale-100 peer-focus:-tw-translate-y-2 peer-focus:tw-scale-75"
        >
          Message (Optional)
        </label>
        <textarea
          tabIndex={10}
          {...register('message')}
          id="input_message"
          className="border-normal tw-z-20 tw-block tw-h-40 tw-w-full tw-rounded-none tw-border tw-border-gray tw-px-2 tw-pb-1.5 tw-pt-2.5 tw-text-sm tw-outline-none focus:tw-border-darkGrey focus:tw-ring-[0.5px] focus:tw-ring-inset focus:tw-ring-darkGrey focus-visible:tw-outline-0 group-[.is-error]:tw-border-error group-[.is-error]:tw-ring-error"
          placeholder=" "
          style={{ height: '6em' }}
          maxLength={300}
        />
      </div>

      <div
        className={beTwMerge('tw-flex tw-items-start', isGB || user?.isEuropeanUnionMember ? 'tw-mb-2.5' : 'tw-mb-4')}
      >
        <input
          {...register('subscribe_to_newsletter')}
          type="checkbox"
          data-tw="checkbox"
          className="border-normal !tw-mt-0 tw-size-[18px] tw-rounded-none tw-border tw-border-[#d9d9d9] tw-text-darkGreen tw-ring-0 checked:tw-border-current focus:!tw-outline-0 focus:tw-ring-0"
          id="send_email"
          onClick={() => setIsSubscribed(s => !s)}
          checked={isSubscribed}
        />
        <label htmlFor="send_email" className="tw-relative tw-top-0.5 tw-mb-0 tw-ms-2 tw-text-xs tw-font-normal">
          {isGB || user?.isEuropeanUnionMember
            ? 'Send me email updates on new styles and special offers from Brilliant Earth. I may withdraw my consent at any time through my account settings or by writing to privacy@brilliantearth.com.'
            : 'Send me updates on new styles and special offers.'}
        </label>
      </div>
      {(isGB || user?.isEuropeanUnionMember) && (
        <div className="tw-mb-4 tw-flex tw-items-start">
          <input
            type="checkbox"
            data-tw="checkbox"
            {...register('privacy', {
              required: 'Agreement to our Privacy and Terms and Contidions is required.',
            })}
            id="agree_policy_checkbox"
            className={beTwMerge(
              'border-normal !tw-mt-0 tw-size-[18px] tw-rounded-none tw-border tw-border-[#d9d9d9] tw-text-darkGreen tw-ring-0 checked:tw-border-current focus:!tw-outline-0 focus:tw-ring-0',
              errors?.privacy?.message && 'tw-border-error'
            )}
            checked={agreement.isAgree}
            onClick={() => setAgreement(v => ({ ...v, isAgree: !v.isAgree }))}
          />
          <div className="tw-ms-2">
            <label htmlFor="agree_policy_checkbox" className="tw-relative tw-top-0.5 tw-mb-0 tw-text-xs tw-font-normal">
              I agree to Brilliant Earth&apos;s{' '}
              <a href="/en-gb/security-and-privacy-policies/#privacy" className="td-u2">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a href="/en-gb/terms-and-conditions/" className="td-u2">
                Terms and Conditions.
              </a>
              *
            </label>
            {!agreement.hasAgreed && !agreement.isAgree && (
              <div className="tw-mt-2 tw-mb-2 tw-text-[11px] tw-text-error">
                Agreement to our Privacy Policy and Terms and Conditions is required.
              </div>
            )}
          </div>
        </div>
      )}
      <Button
        text={btnTxt}
        onClick={validateForm}
        newTab={false}
        hover="darkGreen"
        backgroundColor="darkGreen"
        className="tw-mb-be-2 tw-mr-be-4 tw-border-0"
      />
    </form>
  );
}
